import React from 'react';
import ReactDOM from 'react-dom';
import { _T, C } from '../../shared/theme';
import { GAME_DEFS } from './constants';
import { getGameLevel, loadGameHistory } from '../../shared/utils';

// Pre-create portal container once at module load so it's always in the DOM.
// This avoids the useEffect timing issue where the first render was invisible
// (div not yet appended to body) causing "two clicks to open".
let _portalRoot = null;
function getPortalRoot() {
  if (!_portalRoot || !document.body.contains(_portalRoot)) {
    _portalRoot = document.createElement('div');
    _portalRoot.id = 'sl-game-end-portal';
    document.body.appendChild(_portalRoot);
  }
  return _portalRoot;
}

// Focus / Mental impact per category
const CAT_IMPACT = {
  memory:     { focus: 8,  mental: 12 },
  reaction:   { focus: 10, mental: 6  },
  focus:      { focus: 14, mental: 8  },
  cognition:  { focus: 10, mental: 14 },
  relaxation: { focus: 6,  mental: 4  },
  social:     { focus: 5,  mental: 10 },
  sound:      { focus: 8,  mental: 10 },
};

// ── Sparkline ─────────────────────────────────────────────────────────────────
function Sparkline({ history, color }) {
  if (!history || history.length < 2) return null;
  const pts  = history.slice(-8);
  const W = 200, H = 48, pad = 6;
  const scores = pts.map(p => p.score);
  const min  = Math.min(...scores);
  const max  = Math.max(...scores);
  const range = max - min || 1;
  const xs = pts.map((_, i) => pad + (i / (pts.length - 1)) * (W - pad * 2));
  const ys = scores.map(s => H - pad - ((s - min) / range) * (H - pad * 2));
  const poly = xs.map((x, i) => `${x},${ys[i]}`).join(' ');
  const fill = xs.map((x, i) => `${x},${ys[i]}`).join(' ')
    + ` ${xs[xs.length - 1]},${H} ${xs[0]},${H}`;
  const last = { x: xs[xs.length - 1], y: ys[ys.length - 1] };

  return (
    <svg width={W} height={H} style={{ display: 'block', margin: '0 auto' }}>
      <defs>
        <linearGradient id="sg" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={fill} fill="url(#sg)" />
      <polyline points={poly} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last.x} cy={last.y} r={4} fill={color} />
    </svg>
  );
}

// ── GameEndModal ──────────────────────────────────────────────────────────────
export default function GameEndModal({ gameId, score, lang, isQuit, onAgain, onNext }) {
  const nl = lang === 'nl';

  const gd      = GAME_DEFS.find(g => g.id === gameId) || {};
  const color   = isQuit ? '#E05555' : (gd.color || _T.accent);
  const history = loadGameHistory(gameId);
  const { xp, level } = getGameLevel(gameId);
  const xpInLevel = xp % 100;
  const xpToNext  = 100 - xpInLevel;

  // Impact: positive for natural finish, negative for quit
  const baseImpact = CAT_IMPACT[gd.catGroup] || { focus: 8, mental: 8 };
  const impact = isQuit
    ? { focus: -Math.round(baseImpact.focus * 0.6), mental: -Math.round(baseImpact.mental * 0.5) }
    : baseImpact;

  // Next unlock teaser: cheapest locked game above current total XP
  const nextUnlock = GAME_DEFS
    .filter(g => g.unlockXp && g.unlockXp > xp && !g.multiplayerOnly)
    .sort((a, b) => a.unlockXp - b.unlockXp)[0];

  const modal = (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9000,
      display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      background: 'rgba(5,10,22,0.72)',
      animation: 'gem-bg-in 0.25s ease both',
    }}>
      <style>{`
        @keyframes gem-bg-in { from { opacity:0 } to { opacity:1 } }
        @keyframes gem-slide { from { transform:translateY(100%) } to { transform:translateY(0) } }
      `}</style>

      <div style={{
        width: '100%', maxWidth: 480,
        background: `linear-gradient(175deg, ${_T.bg2} 0%, ${_T.bg} 100%)`,
        borderTop: `2px solid ${color}55`,
        borderRadius: '20px 20px 0 0',
        padding: '28px 24px 40px',
        animation: 'gem-slide 0.38s cubic-bezier(.22,1,.36,1) both',
        boxShadow: `0 -8px 40px ${color}22`,
      }}>

        {/* Header: icon + name + score */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
          <div style={{
            width: 52, height: 52, borderRadius: 14,
            background: `${color}22`, border: `1.5px solid ${color}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 26, flexShrink: 0,
          }}>
            {gd.icon || '🎮'}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 17, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {nl ? (gd.labelNL || gd.label) : (gd.label || gameId)}
            </div>
            <div style={{ color: `${color}cc`, fontSize: 12, fontWeight: 600, marginTop: 2 }}>
              {isQuit
                ? (nl ? 'Vroegtijdig gestopt' : 'Quit early')
                : (nl ? 'Sessie afgerond' : 'Session complete')}
            </div>
          </div>
          {score != null && (
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ color, fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{score}</div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: 11 }}>{nl ? 'score' : 'score'}</div>
            </div>
          )}
        </div>

        {/* Sparkline — last 8 sessions */}
        {history.length >= 2 && (
          <div style={{
            background: `${color}0d`, border: `1px solid ${color}22`,
            borderRadius: 12, padding: '12px 8px 8px', marginBottom: 16,
          }}>
            <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', textAlign: 'center', marginBottom: 6 }}>
              {nl ? 'Laatste 8 sessies' : 'Last 8 sessions'}
            </div>
            <Sparkline history={history} color={color} />
          </div>
        )}

        {/* Focus / Mental impact tiles */}
        {isQuit && (
          <div style={{ background: 'rgba(224,85,85,0.08)', border: '1px solid rgba(224,85,85,0.25)', borderRadius: 12, padding: '8px 14px', marginBottom: 12, fontSize: 12, color: 'rgba(224,85,85,0.85)', lineHeight: 1.5 }}>
            ⚠️ {nl ? 'Je hebt de sessie vroegtijdig gestopt. Dit heeft een negatief effect op je focusscore.' : "You quit before the game ended. This reduces your focus score."}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
          {[
            { label: nl ? 'Focus' : 'Focus',   icon: '🧠', val: impact.focus,  c: isQuit ? '#E05555' : '#4AC87A' },
            { label: nl ? 'Mentaal' : 'Mental', icon: '💡', val: impact.mental, c: isQuit ? '#D4873A' : '#7B6FD0' },
          ].map(tile => (
            <div key={tile.label} style={{
              background: `${tile.c}12`, border: `1px solid ${tile.c}30`,
              borderRadius: 12, padding: '10px 14px',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <span style={{ fontSize: 20 }}>{tile.icon}</span>
              <div>
                <div style={{ color: tile.c, fontSize: 15, fontWeight: 800 }}>{tile.val > 0 ? '+' : ''}{tile.val}%</div>
                <div style={{ color: 'rgba(255,255,255,0.45)', fontSize: 10, fontWeight: 600 }}>{tile.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Level XP bar */}
        <div style={{ marginBottom: nextUnlock ? 14 : 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
            <span style={{ color: 'rgba(255,255,255,0.55)', fontSize: 11, fontWeight: 700 }}>
              Level {level}
            </span>
            <span style={{ color, fontSize: 11, fontWeight: 700 }}>
              {xpInLevel}/100 XP
            </span>
          </div>
          <div style={{ height: 7, background: 'rgba(255,255,255,0.08)', borderRadius: 4, overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${xpInLevel}%`,
              background: `linear-gradient(90deg, ${color}88, ${color})`,
              borderRadius: 4,
            }} />
          </div>
          <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 10, marginTop: 4, textAlign: 'right' }}>
            {xpToNext} XP {nl ? 'naar level' : 'to level'} {level + 1}
          </div>
        </div>

        {/* Next unlock teaser — only for natural finishes */}
        {!isQuit && nextUnlock && (
          <div style={{
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
            borderRadius: 12, padding: '10px 14px',
            display: 'flex', alignItems: 'center', gap: 10,
            marginBottom: 20,
          }}>
            <span style={{ fontSize: 20, opacity: 0.4 }}>{nextUnlock.icon}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
                {nl ? 'Volgende unlock' : 'Next unlock'}
              </div>
              <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 600, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {nl ? (nextUnlock.labelNL || nextUnlock.label) : nextUnlock.label}
              </div>
            </div>
            <div style={{ color: _T.accent, fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>
              {nextUnlock.unlockXp - xp} XP
            </div>
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <button
            onClick={onNext}
            style={{
              padding: '13px 0', borderRadius: 13,
              background: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.14)',
              color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            {nl ? 'Overzicht' : 'Overview'}
          </button>
          <button
            onClick={onAgain}
            style={{
              padding: '13px 0', borderRadius: 13,
              background: `linear-gradient(135deg, ${_T.accent}, #e2c97e)`,
              border: 'none',
              color: '#0a1220', fontSize: 14, fontWeight: 800,
              cursor: 'pointer',
              boxShadow: `0 4px 16px ${_T.accent}55`,
            }}
          >
            {nl ? 'Opnieuw' : 'Again'} ↺
          </button>
        </div>

      </div>
    </div>
  );

  return ReactDOM.createPortal(modal, getPortalRoot());
}
